import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminApi, formatApiError } from "../../lib/api";
import { Field, inputCls, UploadField } from "../../components/admin/fields";
import { toast } from "sonner";

const EMPTY = {
  title: "", slug: "", publisher_id: "", category: "", language: "English", frequency: "Monthly",
  short_description: "", long_description: "", cover_image: "", sample_images: [], issue_pdf: "",
  issn: "", status: "draft", seo_title: "", seo_description: "", seo_keywords: "",
  featured: false, rating: 4.5, rating_count: 0, preview_pages: 4,
};

export default function MagazineForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [pubs, setPubs] = useState([]);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    adminApi.get("/publishers").then((r) => setPubs(r.items));
    adminApi.get("/categories").then((r) => setCats(r.items));
    if (id) adminApi.get(`/magazines/${id}`).then(setForm).catch(() => {});
  }, [id]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, slug: form.slug || undefined, publisher_id: form.publisher_id || null };
      if (id) await adminApi.put(`/magazines/${id}`, payload);
      else await adminApi.post("/magazines", payload);
      toast.success("Magazine saved.");
      navigate("/apadmin/magazines");
    } catch (err) {
      toast.error(formatApiError(err));
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-3xl" data-testid="magazine-form">
      <h1 className="text-xl font-bold text-zinc-900 mb-6">{id ? "Edit Magazine" : "New Magazine"}</h1>
      <div className="bg-white border border-zinc-200 rounded-md p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Title *"><input required value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid="mag-title-input" /></Field>
        <Field label="Slug (auto if empty)"><input value={form.slug || ""} onChange={(e) => set("slug", e.target.value)} className={inputCls} data-testid="mag-slug-input" /></Field>
        <Field label="Publisher">
          <select value={form.publisher_id || ""} onChange={(e) => set("publisher_id", e.target.value)} className={inputCls} data-testid="mag-publisher-select">
            <option value="">— Select —</option>
            {pubs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Category">
          <select value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} data-testid="mag-category-select">
            <option value="">— Select —</option>
            {cats.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Language"><input value={form.language} onChange={(e) => set("language", e.target.value)} className={inputCls} data-testid="mag-language-input" /></Field>
        <Field label="Frequency">
          <select value={form.frequency} onChange={(e) => set("frequency", e.target.value)} className={inputCls} data-testid="mag-frequency-select">
            {["Weekly", "Fortnightly", "Monthly", "Quarterly"].map((f) => <option key={f}>{f}</option>)}
          </select>
        </Field>
        <Field label="ISSN"><input value={form.issn} onChange={(e) => set("issn", e.target.value)} className={inputCls} data-testid="mag-issn-input" /></Field>
        <Field label="Status">
          <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls} data-testid="mag-status-select">
            <option value="draft">Draft</option><option value="published">Published</option><option value="coming_soon">Coming Soon</option>
          </select>
        </Field>
        <div className="sm:col-span-2"><Field label="Short description"><input value={form.short_description} onChange={(e) => set("short_description", e.target.value)} className={inputCls} data-testid="mag-short-desc-input" /></Field></div>
        <div className="sm:col-span-2"><Field label="Long description"><textarea rows={4} value={form.long_description} onChange={(e) => set("long_description", e.target.value)} className={inputCls} data-testid="mag-long-desc-input" /></Field></div>
        <UploadField label="Cover image" value={form.cover_image} onChange={(v) => set("cover_image", v)} accept="image/*" testId="mag-cover-upload" />
        <UploadField label="Issue PDF (reader)" value={form.issue_pdf} onChange={(v) => set("issue_pdf", v)} accept="application/pdf" testId="mag-pdf-upload" />
        <Field label="Rating"><input type="number" step="0.1" min="0" max="5" value={form.rating} onChange={(e) => set("rating", parseFloat(e.target.value) || 0)} className={inputCls} data-testid="mag-rating-input" /></Field>
        <Field label="Rating count"><input type="number" value={form.rating_count} onChange={(e) => set("rating_count", parseInt(e.target.value) || 0)} className={inputCls} data-testid="mag-rating-count-input" /></Field>
        <Field label="Free preview pages"><input type="number" min="1" max="20" value={form.preview_pages} onChange={(e) => set("preview_pages", parseInt(e.target.value) || 4)} className={inputCls} data-testid="mag-preview-input" /></Field>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2 text-sm text-zinc-700">
            <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-zinc-900" data-testid="mag-featured-check" /> Featured on home page
          </label>
        </div>
        <div className="sm:col-span-2"><Field label="SEO title"><input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} className={inputCls} data-testid="mag-seo-title-input" /></Field></div>
        <div className="sm:col-span-2"><Field label="SEO meta description"><textarea rows={2} value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} className={inputCls} data-testid="mag-seo-desc-input" /></Field></div>
        <div className="sm:col-span-2"><Field label="SEO keywords"><input value={form.seo_keywords} onChange={(e) => set("seo_keywords", e.target.value)} className={inputCls} data-testid="mag-seo-keywords-input" /></Field></div>
      </div>
      <div className="flex gap-3 mt-6">
        <button type="submit" disabled={saving} data-testid="mag-save-btn"
          className="bg-zinc-900 text-white text-sm font-semibold px-6 py-2.5 rounded-md hover:bg-zinc-700 disabled:opacity-60">
          {saving ? "Saving..." : "Save Magazine"}
        </button>
        <button type="button" onClick={() => navigate("/apadmin/magazines")} data-testid="mag-cancel-btn"
          className="text-sm font-semibold px-6 py-2.5 rounded-md border border-zinc-300 hover:border-zinc-900">Cancel</button>
      </div>
    </form>
  );
}
